import React, { useEffect } from "react";
import { useFieldArray, Control, FieldErrors, useWatch } from "react-hook-form";
import { Button, Card, Col, Row } from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { InputField, ReactSelectField } from "../../atoms/FormElement";
import { useDropdownOptions } from "../../../hooks/useFormattedSelectOptions";
import { PopupConfirm } from "../../atoms/Popup";

interface BankDetailSectionProps {
  control: Control<any>;
  errors: FieldErrors;
  configData: any;
  getValidationProps: Function;
  loading?: boolean;
  ownerId?: string;
  result?: any;
}

const AddOwnerBankDetail: React.FC<BankDetailSectionProps> = ({
  control,
  errors,
  configData,
  getValidationProps,
  loading,
  ownerId,
  result,
}) => {
  const { fields, append, remove, update, replace } = useFieldArray({
    control,
    name: "beneficiaries",
  });

  const bankOptions = useDropdownOptions(configData?.bankList);
  // Watch the array to reflect isDeleted state changes
  const beneficiaries = useWatch({ control, name: "beneficiaries" });
  const defaultProps = { control };

  const RemoveAndDeleteBeneficiary = (index) => {
    PopupConfirm({
      title: `Are you sure you want to delete this beneficiary?`,
      onOk: () => {
        ownerId
          ? update(index, {
              ...beneficiaries[index],
              isDeleted: true,
            })
          : remove(index);
      },
      okText: "Yes",
    });
  };

  useEffect(() => {
    const data = result?.result?.data || [];

    if (data.length > 0) {
      // ✅ Only pick required fields
      const formatted =
        data.map((item) => ({
          beneficiaryBank: {
            label:
              item.beneficiaryBank
                ?.toString()
                ?.split("_")
                .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ") || "",
            value: item.beneficiaryBank || "",
          },
          beneficiaryAccountNumber: item.beneficiaryAccountNumber || "",
          beneficiaryIBAN: item.beneficiaryIBAN || "",
          beneficiaryName: item.beneficiaryName || "",
          isDeleted: item?.isDeleted,
        })) || [];
      replace(formatted);
      // resetField("beneficiaries", { defaultValue: formatted });
    }
  }, [result]);

  return (
    <Card loading={loading} title="Bank Accounts" style={{ marginBottom: 10 }}>
      {fields.map((field, index) => {
        const isDeleted = beneficiaries?.[index]?.isDeleted;

        // Skip rendering deleted entries
        if (isDeleted) return null;

        return (
          <div key={field.id}>
            <Row gutter={8} align="middle">
              <Col span={6}>
                <InputField
                  fieldname={`beneficiaries.${index}.beneficiaryName`}
                  label="Beneficiary Name (Account Title)"
                  {...defaultProps}
                  {...getValidationProps({
                    fieldName: `beneficiaries.${index}.beneficiaryName`,
                    errors,
                    isRequired: true,
                  })}
                  iProps={{
                    placeholder: "Enter beneficiary name",
                  }}
                />
              </Col>

              <Col span={6}>
                <ReactSelectField
                  fieldname={`beneficiaries.${index}.beneficiaryBank`}
                  label="Bank"
                  selectOption={bankOptions || []}
                  {...defaultProps}
                  {...getValidationProps({
                    fieldName: `beneficiaries.${index}.beneficiaryBank`,
                    errors,
                    isRequired: true,
                  })}
                  iProps={{
                    placeholder: "Select bank",
                  }}
                />
              </Col>

              <Col span={6}>
                <InputField
                  fieldname={`beneficiaries.${index}.beneficiaryAccountNumber`}
                  label="Account Number"
                  {...defaultProps}
                  {...getValidationProps({
                    fieldName: `beneficiaries.${index}.beneficiaryAccountNumber`,
                    errors,
                    isRequired: true,
                  })}
                  iProps={{
                    placeholder: "Enter account number",
                  }}
                />
              </Col>

              <Col span={5}>
                <InputField
                  fieldname={`beneficiaries.${index}.beneficiaryIBAN`}
                  label="IBAN"
                  {...defaultProps}
                  {...getValidationProps({
                    fieldName: `beneficiaries.${index}.beneficiaryIBAN`,
                    errors,
                    isRequired: true,
                  })}
                  iProps={{
                    placeholder: "Enter IBAN",
                  }}
                />
              </Col>

              <Col span={1} style={{ display: "flex", alignItems: "center" }}>
                {
                  <MinusCircleOutlined
                    style={{ color: "red", fontSize: 18, cursor: "pointer" }}
                    onClick={() => RemoveAndDeleteBeneficiary(index)}
                    // Instead of remove — mark as deleted
                  />
                }
              </Col>
            </Row>
          </div>
        );
      })}

      <Button
        type="dashed"
        onClick={() =>
          append({
            beneficiaryBank: "",
            beneficiaryAccountNumber: "",
            beneficiaryIBAN: "",
            beneficiaryName: "",
            isDeleted: false,
          })
        }
        icon={<PlusOutlined />}
        block
      >
        Add Bank
      </Button>
    </Card>
  );
};

export default AddOwnerBankDetail;
